import { type ComponentPropsWithoutRef } from 'react';
import type { AvatarData } from '@/api/gen/_model/avatarData';
import { type PlanType } from '@/types';
import { type AvatarShape, type AvatarSize, SIZE_PIXEL_MAP } from '../consts/avatar.constants';
import { BakedAvatar } from './BakedAvatar';
import { FallbackAvatar } from './FallbackAvatar';
import { SpriteAvatar } from './SpriteAvatar';
type BaseUserAvatarProps = ComponentPropsWithoutRef<'div'> & {
  avatarData?: AvatarData | null;
  planType?: PlanType;
  backgroundColor?: string;
  /** 기본 원형. 시안이 라운드 사각형(rounded-sm)을 지정한 자리에서만 'rounded'. */
  shape?: AvatarShape;
};
type BakedUserAvatarProps = BaseUserAvatarProps & {
  useSprite?: false | undefined;
  size?: AvatarSize;
  spritePixelSize?: never;
};
type SpriteBySizeUserAvatarProps = BaseUserAvatarProps & {
  useSprite: true;
  size?: AvatarSize;
  spritePixelSize?: never;
};
type SpriteByPixelUserAvatarProps = BaseUserAvatarProps & {
  useSprite: true;
  size?: never;
  spritePixelSize: number;
};
type UserAvatarProps = BakedUserAvatarProps | SpriteBySizeUserAvatarProps | SpriteByPixelUserAvatarProps;
export const UserAvatar = ({
  avatarData,
  size,
  shape = 'circle',
  planType,
  useSprite,
  backgroundColor,
  spritePixelSize,
  className,
  style,
  ...props
}: UserAvatarProps) => {
  if (useSprite) {
    const resolvedSpritePixelSize = spritePixelSize ?? SIZE_PIXEL_MAP[size ?? 'xs'];
    if (!avatarData) {
      return <FallbackAvatar size={size ?? 'xs'} shape={shape} planType={planType} backgroundColor={backgroundColor} className={className} style={{
        width: resolvedSpritePixelSize,
        height: resolvedSpritePixelSize,
        ...style
      }} {...props} />;
    }
    return <SpriteAvatar avatarData={avatarData} backgroundColor={backgroundColor} spritePixelSize={resolvedSpritePixelSize} shape={shape} className={className} style={style} {...props} />;
  }
  const resolvedSize = size ?? 'xs';
  if (!avatarData) {
    return <FallbackAvatar size={resolvedSize} shape={shape} planType={planType} backgroundColor={backgroundColor} className={className} style={style} {...props} />;
  }
  return <BakedAvatar avatarData={avatarData} size={resolvedSize} shape={shape} planType={planType} backgroundColor={backgroundColor} className={className} style={style} {...props} data-sentry-element="BakedAvatar" data-sentry-component="UserAvatar" data-sentry-source-file="UserAvatar.tsx" />;
};